import type { Bonus } from '../types/content'

export const bonuses: Bonus[] = [
  {
    id: '01',
    title: '150 Swimming Drills Library',
    headline:
      'A Complete Drill Bank for Water Confidence, Breathing, Floating, Kicking, Every Major Stroke, Starts, Turns & Endurance',
    intro:
      'Instead of repeating the same five or ten drills every week, you’ll have a complete library of 150 practical swimming drills organized by skill and objective.',
    groups: [
      {
        title: 'Categories included',
        items: [
          'Water confidence, breathing and floating',
          'Body position & streamline, kicking',
          'Freestyle, backstroke, breaststroke, butterfly',
          'Starts & push-offs, turns, endurance',
        ],
      },
      {
        title: 'Every drill includes',
        items: [
          'Objective, recommended level and equipment',
          'Setup and step-by-step execution',
          'Instructor cues and common mistakes',
          'Easier variation, advanced variation and progression criteria',
        ],
      },
    ],
    benefit:
      'Need another way to teach a kicking skill, an easier variation, or an extra activity to complete the session? Open the library — 150 practical options ready when you need them.',
  },
  {
    id: '02',
    title: '100 Swimming Mistakes, Teaching Cues & Corrections',
    headline: 'The Instructor’s Practical Troubleshooting Guide',
    intro:
      'Seeing a mistake is one thing. Knowing why it is happening, what to say, and what exercise to use next is something else. This guide covers 100 common swimming mistakes across the most important swimming skills.',
    groups: [
      {
        title: 'Covers problems related to',
        items: [
          'Breathing, floating and body position',
          'Kicking and propulsion',
          'Freestyle, backstroke, breaststroke and butterfly',
          'Timing and coordination',
        ],
      },
      {
        title: 'Each mistake explains',
        items: [
          'What you see and what may be causing it',
          'Why it happens',
          'A short teaching cue and the recommended correction',
          'A corrective drill, a regression and a progression',
        ],
      },
    ],
    benefit:
      'Instead of “something looks wrong”, you move to: I see the problem, I know the likely cause, I know what cue to give and what drill to use.',
  },
  {
    id: '03',
    title: '50 Ready-to-Teach Swim Games & Fun Lessons',
    headline: 'Skill-Based Games and Challenges That Keep Swimmers Engaged',
    intro:
      'Swimming lessons should be structured — but structured does not have to mean repetitive. This bonus gives you 50 ready-to-teach games, challenges, and activities designed to reinforce actual swimming skills.',
    groups: [
      {
        title: 'Activities cover',
        items: [
          'Water confidence and breathing',
          'Floating & balance, kicking',
          'Stroke development and coordination',
          'Teamwork and class engagement',
        ],
      },
      {
        title: 'Every activity includes',
        items: [
          'Objective, recommended level and group size',
          'Equipment, pool setup and instructions',
          'Teaching cues and safety considerations',
          'Easier version, harder version and the skill being reinforced',
        ],
      },
    ],
    benefit:
      'More ways to keep sessions fresh, reinforce skills, and maintain participation without abandoning the objective of the lesson.',
  },
  {
    id: '04',
    title: 'Swimmer Assessment, Skill Level & Progress Tracking System',
    headline: 'Stop Guessing What a Swimmer Should Work on Next',
    intro:
      'How confident is the swimmer? Which skills are already consistent? What needs more practice? Are they actually ready for the next progression? This bonus gives you a structured system for observing and recording swimmer development.',
    groups: [
      {
        title: 'Areas you can assess',
        items: [
          'Water confidence, breathing and floating',
          'Body position and kicking',
          'All four competitive strokes',
          'Endurance and independence',
        ],
      },
      {
        title: 'Includes',
        items: [
          'Observation checklists and skill-level criteria',
          'Readiness-to-progress standards',
          'Lesson review forms, progress records and skill charts',
          'Reassessment guidance and next-priority planning',
        ],
      },
    ],
    benefit:
      'Track confidence, technique, endurance and independence to get a clearer picture of development and guide future lessons.',
  },
  {
    id: '05',
    title: '40 Emergency Lessons, Small-Group & Limited-Equipment Plans',
    headline: 'Ready-to-Teach Backup Lessons for Real Teaching Situations',
    intro:
      'A planned swimming lesson does not always go exactly as expected. To be clear: “emergency lessons” means emergency lesson-planning situations — not lifeguard or rescue training.',
    groups: [
      {
        title: 'Situations covered',
        items: [
          'Last-minute schedule changes and substitute teaching',
          'Small groups and mixed abilities',
          'Crowded pools and limited lane space',
          'Limited equipment, no-equipment alternatives and quick skill reviews',
        ],
      },
      {
        title: 'Every lesson includes',
        items: [
          'Objective, level, duration and equipment',
          'Pool setup, warm-up and exact lesson structure',
          'Drills and teaching cues',
          'Easier and harder adaptations, class-management notes and assessment points',
        ],
      },
    ],
    benefit:
      'When your original plan no longer works, you still have a practical lesson ready to teach.',
  },
]
